import React, { Fragment } from 'react'
import { Pressable, StyleSheet } from 'react-native'
import { DNAText } from '@alucio/lux-ui'
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors'
import { CommonIcon } from 'src/components/DNA'
import { FolderORM } from 'src/types/types'

const S = StyleSheet.create({
  chevronIcon: {
    color: colors['color-ui2-neutral-400'],
    height: 14,
    width: 14,
    marginLeft: 8,
    marginRight: 8,
    paddingTop: 2,
  },
  breadcrumb: {
    fontSize: 14,
    fontWeight: '400',
  },
  currentFolder: {
    fontSize: 14,
    fontWeight: '700',
  },
})

export interface DNABreadcrumbProps {
  /** The root title to display (e.g., "My Folders", "Shared With Me") */
  rootTitle: string
  /** Array of folder objects to display as breadcrumb items */
  folderStack: FolderORM[]
  /** Callback when root item is pressed */
  onRootPress: () => void
  /** Callback when a folder item is pressed */
  onFolderPress: (folderORM: FolderORM) => void
  /** Test ID for the root breadcrumb item */
  rootTestID?: string
}

/**
 * A reusable breadcrumb component for folder navigation
 *
 * @example
 * ```tsx
 * <DNABreadcrumb
 *   rootTitle="My Folders"
 *   folderStack={folderNav.folderStack}
 *   onRootPress={() => folderNavURL.popToFolder()}
 *   onFolderPress={(folderORM) => folderNavURL.popToFolder(folderORM)}
 *   rootTestID="page-title"
 * />
 * ```
 */
const DNABreadcrumb: React.FC<DNABreadcrumbProps> = ({
  rootTitle,
  folderStack,
  onRootPress,
  onFolderPress,
  rootTestID = 'breadcrumb-root',
}) => {
  return (
    <>
      <Pressable onPress={onRootPress}>
        <DNAText testID={rootTestID} status="secondary" style={S.breadcrumb}>
          {rootTitle}
        </DNAText>
      </Pressable>
      {folderStack.map((folderORM, index) => (
        <Fragment key={folderORM.model.id}>
          <CommonIcon variant="caret-right" style={S.chevronIcon} />
          <Pressable onPress={() => onFolderPress(folderORM)} style={{ flexShrink: 100 }}>
            <DNAText
              status="secondary"
              style={index === folderStack.length - 1 ? S.currentFolder : S.breadcrumb}
              ellipsizeMode="tail"
              numberOfLines={1}
            >
              {folderORM.model.name}
            </DNAText>
          </Pressable>
        </Fragment>
      ))}
    </>
  )
}

export default DNABreadcrumb
